import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  FileText, Download, Sparkles, Clock, TrendingUp,
  Calendar, Star, Activity,
} from 'lucide-react';

/**
 * Phase 312 — Coach Performance Scorecards UI
 *
 * Owner-facing dashboard for the Phase 311 AI scorecard pipeline:
 *  • Hero: "Generate Current Month Scorecards" button → POST /api/scorecards/run-now
 *  • Filter strip (month) + summary tiles
 *  • Grouped table (by month) of every generated scorecard, with a
 *    "Download PDF" action linking to /api/scorecards/{id}.pdf
 *  • Beautiful empty state when no scorecards exist yet
 */

function MetricTile({ icon: Icon, label, value, sub, accent = 'emerald', testId }) {
  const accentMap = {
    emerald: 'bg-emerald-100 text-emerald-700',
    amber:   'bg-amber-100   text-amber-700',
    blue:    'bg-blue-100    text-blue-700',
    rose:    'bg-rose-100    text-rose-700',
  };
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">{label}</span>
          <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accentMap[accent] || accentMap.emerald}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="text-2xl font-display font-semibold tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function npsBadgeVariant(avg) {
  if (avg >= 4.5) return { color: 'bg-emerald-100 text-emerald-800', label: 'Excellent' };
  if (avg >= 4.0) return { color: 'bg-teal-100 text-teal-800',         label: 'Strong' };
  if (avg >= 3.0) return { color: 'bg-amber-100 text-amber-800',       label: 'OK' };
  if (avg > 0)    return { color: 'bg-rose-100 text-rose-800',         label: 'At risk' };
  return            { color: 'bg-slate-100 text-slate-600',            label: 'No data' };
}

function currentMonthKey() {
  // IST clock — front-end approximates by reading the user's local clock
  // then offsetting to IST (+5h30m). Good enough for "what month am I in".
  const now = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key) {
  if (!key) return '';
  const [year, month] = key.split('-').map(Number);
  if (!year || !month) return key;
  const dt = new Date(Date.UTC(year, month - 1, 1));
  return dt.toLocaleString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}


export default function CoachScorecards() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [monthFilter, setMonthFilter] = useState('all');

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  const fetchScorecards = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/scorecards/list');
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load scorecards';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchScorecards(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    const month = currentMonthKey();
    // Optimistic toast — give the user something to look at while the
    // backend churns through metric aggregation + Claude calls + PDF gen.
    const toastId = toast.loading('Generating scorecards… this can take 10-15 seconds for a full academy.');
    try {
      const { data } = await api.post('/scorecards/run-now', { month, skip_whatsapp: false });
      if (data?.ok) {
        toast.success(`Generated ${data.generated || 0} scorecard${(data.generated || 0) === 1 ? '' : 's'} for ${monthLabel(month)}.`, { id: toastId });
        await fetchScorecards();
      } else {
        toast.error(data?.reason || 'Generation completed but no scorecards were created.', { id: toastId });
      }
    } catch (err) {
      // Front-end safety net: if the request timed out OR the backend
      // returned an error, surface a useful message instead of crashing
      // the React tree.
      if (err?.code === 'ECONNABORTED' || err?.message?.toLowerCase().includes('timeout')) {
        toast.warning('Generation is taking longer than expected. Check back in a minute!', { id: toastId });
      } else {
        const msg = err?.response?.data?.detail || err?.message || 'Failed to start generation';
        toast.error(msg, { id: toastId });
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (scorecard) => {
    // Open the PDF stream in a new tab. We append the JWT via the api
    // helper isn't automatic for native <a> clicks, so we use the
    // axios instance with `responseType: 'blob'` and trigger a
    // download via an object URL.
    api.get(`/scorecards/${scorecard.id}.pdf`, { responseType: 'blob' })
      .then((res) => {
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `scorecard_${(scorecard.coach_name || 'coach').replace(/\s+/g, '_')}_${scorecard.month}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Failed to download PDF'));
  };

  // Distinct months for the filter strip.
  const monthsAvailable = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.month && set.add(r.month));
    return Array.from(set).sort().reverse();
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (monthFilter === 'all') return rows;
    return rows.filter((r) => r.month === monthFilter);
  }, [rows, monthFilter]);

  const grouped = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const key = r.month || 'unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredRows]);

  // Summary stats for the metric tiles.
  const stats = useMemo(() => {
    if (!rows.length) return { coaches: 0, avgNps: 0, totalSessions: 0, totalHours: 0 };
    const uniqueCoaches = new Set(rows.map((r) => r.coach_id)).size;
    const npsValues = rows.map((r) => Number(r.avg_nps) || 0).filter((x) => x > 0);
    const avgNps = npsValues.length ? npsValues.reduce((a, b) => a + b, 0) / npsValues.length : 0;
    const totalSessions = rows.reduce((a, r) => a + (Number(r.sessions_conducted) || 0), 0);
    const totalHours = rows.reduce((a, r) => a + (Number(r.hours_worked) || 0), 0);
    return {
      coaches: uniqueCoaches,
      avgNps: avgNps.toFixed(2),
      totalSessions,
      totalHours: totalHours.toFixed(1),
    };
  }, [rows]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="scorecards-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-72 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coach-scorecards-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            <h1 className="text-2xl font-display font-semibold">Coach Performance Scorecards</h1>
          </div>
          <p className="text-sm text-muted-foreground max-w-xl">
            AI-generated monthly performance reviews. Auto-fires on the 1st of every month
            at 09:00 IST, or trigger an off-cycle run below for a mid-month review.
          </p>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={generating}
          className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
          data-testid="generate-scorecards-button"
        >
          {generating ? (
            <>
              <Clock className="h-4 w-4 mr-2 animate-spin" />
              Generating…
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate {monthLabel(currentMonthKey())} Scorecards
            </>
          )}
        </Button>
      </div>

      {/* Metric tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="scorecard-summary-tiles">
        <MetricTile testId="tile-coaches"  icon={Activity}   label="Coaches Reviewed" value={stats.coaches} sub="across all months" />
        <MetricTile testId="tile-avg-nps"  icon={Star}        label="Avg Parent NPS"   value={`${stats.avgNps}/5`} sub="lifetime average" accent="amber" />
        <MetricTile testId="tile-sessions" icon={Calendar}    label="Sessions"          value={stats.totalSessions} sub="conducted total" accent="blue" />
        <MetricTile testId="tile-hours"    icon={TrendingUp}  label="Hours"             value={`${stats.totalHours}h`} sub="logged total" accent="emerald" />
      </div>

      {/* Month filter */}
      {monthsAvailable.length > 1 && (
        <Tabs value={monthFilter} onValueChange={setMonthFilter}>
          <TabsList data-testid="scorecard-month-tabs">
            <TabsTrigger value="all" data-testid="month-filter-all">All months</TabsTrigger>
            {monthsAvailable.slice(0, 6).map((m) => (
              <TabsTrigger key={m} value={m} data-testid={`month-filter-${m}`}>{monthLabel(m)}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {/* Body */}
      {rows.length === 0 ? (
        // Empty state — beautiful, action-oriented.
        <Card data-testid="scorecards-empty-state">
          <CardContent className="py-16 px-6 text-center">
            <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mb-5">
              <FileText className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-lg font-display font-semibold mb-1">No scorecards generated yet</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
              Automate your monthly performance reviews. AI aggregates each coach&apos;s
              hours, NPS, sessions, and voice logs — then drafts a 3-sentence head-coach
              review delivered via WhatsApp as a one-page PDF.
            </p>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="empty-state-generate-button"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {generating ? 'Generating…' : `Generate ${monthLabel(currentMonthKey())} Scorecards`}
            </Button>
          </CardContent>
        </Card>
      ) : (
        // Grouped tables (one per month).
        grouped.map(([month, items]) => (
          <Card key={month} data-testid={`scorecard-group-${month}`}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-emerald-600" />
                {monthLabel(month)}
                <Badge variant="outline" className="ml-1">{items.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Coach</TableHead>
                      <TableHead className="text-right">Sessions</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Voice Logs</TableHead>
                      <TableHead>Avg NPS</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((row) => {
                      const nps = Number(row.avg_nps) || 0;
                      const npsB = npsBadgeVariant(nps);
                      return (
                        <TableRow key={row.id} data-testid={`scorecard-row-${row.id}`}>
                          <TableCell>
                            <div className="font-medium">{row.coach_name || '—'}</div>
                            <div className="text-[11px] text-muted-foreground font-mono">{(row.coach_id || '').slice(0, 8)}…</div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{row.sessions_conducted ?? 0}</TableCell>
                          <TableCell className="text-right tabular-nums">{(Number(row.hours_worked) || 0).toFixed(1)}h</TableCell>
                          <TableCell className="text-right tabular-nums">{row.voice_logs_count ?? 0}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="tabular-nums text-sm">{nps > 0 ? nps.toFixed(2) : '—'}</span>
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${npsB.color}`}>{npsB.label}</span>
                              {Number(row.nps_response_count) > 0 && (
                                <span className="text-[10px] text-muted-foreground">({row.nps_response_count})</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(row)}
                              data-testid={`download-pdf-${row.id}`}
                            >
                              <Download className="h-3.5 w-3.5 mr-1" /> PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
